import { apiFetch } from "../api";
import { theme } from "../theme";
import { useApp } from "../AppContext";
import ProductCard from "../components/ProductCard";

const STAT_LABELS = {
  top3: "v Top 3",
  first: "na 1. místě",
  improved: "zlepšení",
  declined: "poklesy",
};

// ── Produkty: seznam, skupiny jako filtr, hledání, přidání ─────────────────────
export default function ProductsPage() {
  const {
    token, products, setProducts, groups, setGroups, loadGroups,
    loadingProducts, error, activeGroup, setActiveGroup,
    statFilter, setStatFilter, searchQuery, setSearchQuery,
    filteredProducts, setSelectedProduct, setShowGroupModal,
  } = useApp();

  const pill = (active, color) => ({
    padding: "7px 16px", borderRadius: 20, fontSize: 13, fontWeight: 600,
    border: "1.5px solid " + (active ? color : theme.line),
    background: active ? color : theme.bg,
    color: active ? "#fff" : theme.inkSoft,
    cursor: "pointer", fontFamily: "inherit",
  });

  const deleteGroup = async (g) => {
    if (!window.confirm("Smazat skupinu " + g.name + "?")) return;
    try {
      await apiFetch("/groups/" + g.id, token, { method: "DELETE" });
      setGroups(prev => prev.filter(gr => gr.id !== g.id));
      if (activeGroup === g.id) setActiveGroup(null);
    } catch(e) {}
  };

  return (
    <>
      <div className="fadeUp" style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.6px", color: theme.ink, marginBottom: 6 }}>Produkty</h1>
        <p style={{ color: theme.inkSoft, fontSize: 14 }}>
          Sledujete <span style={{ fontVariantNumeric: theme.tnum, fontWeight: 600 }}>{products.length}</span> produkt{products.length === 1 ? "" : products.length < 5 ? "y" : "ů"}
        </p>
      </div>

      {/* Filtr skupin */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={() => setActiveGroup(null)} style={pill(activeGroup === null, theme.blue)}>
          Všechny (<span style={{ fontVariantNumeric: theme.tnum }}>{products.length}</span>)
        </button>
        {groups.map(g => (
          <div key={g.id} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
            onMouseEnter={e => { const b = e.currentTarget.querySelector(".del-group"); if (b) b.style.display = "flex"; }}
            onMouseLeave={e => { const b = e.currentTarget.querySelector(".del-group"); if (b) b.style.display = "none"; }}
          >
            <button onClick={() => setActiveGroup(g.id)} style={{ ...pill(activeGroup === g.id, g.color), display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: activeGroup === g.id ? "#fff8" : g.color }} />
              <span>{g.name} (<span style={{ fontVariantNumeric: theme.tnum }}>{g.product_count}</span>)</span>
            </button>
            <button className="del-group" onClick={e => { e.stopPropagation(); deleteGroup(g); }} style={{ display: "none", position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: theme.bad, border: "none", color: "#fff", fontSize: 11, cursor: "pointer", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1 }}>
              ✕
            </button>
          </div>
        ))}
        {/* oddělovač: vlevo filtry, vpravo akce — ať se nepletou */}
        <span aria-hidden="true" style={{ width: 1, height: 20, alignSelf: "center", background: theme.line, margin: "0 6px" }} />
        <button onClick={() => setShowGroupModal(true)} style={{ padding: "7px 16px", borderRadius: theme.rBtn, fontSize: 13, fontWeight: 600, border: `1.5px dashed ${theme.blueLine}`, background: "transparent", color: theme.blue, cursor: "pointer", fontFamily: "inherit" }}>
          + Nová skupina
        </button>
      </div>

      {/* Aktivní filtry (hledání / statistika) */}
      {(statFilter || searchQuery) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: theme.mut }}>Filtr</span>
          {statFilter && (
            <button onClick={() => setStatFilter(null)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, border: `1px solid ${theme.blueLine}`, background: theme.blueSoft, color: theme.blue, cursor: "pointer", fontFamily: "inherit" }}>
              {STAT_LABELS[statFilter] || statFilter} ✕
            </button>
          )}
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, border: `1px solid ${theme.blueLine}`, background: theme.blueSoft, color: theme.blue, cursor: "pointer", fontFamily: "inherit" }}>
              „{searchQuery}" ✕
            </button>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: theme.badSoft, border: `1.5px solid ${theme.badLine}`, borderRadius: theme.rCard, padding: "14px 18px", marginBottom: 20, color: theme.bad, fontSize: 14 }}>
          {error}
        </div>
      )}

      {loadingProducts ? (
        <div style={{ textAlign: "center", padding: 60, color: theme.mut }}>Načítám produkty…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filteredProducts.map((product, i) => (
            <div key={product.id} className="cardAnim" style={{ animationDelay: `${Math.min(i, 12) * 70}ms` }}>
              <ProductCard
                product={product}
                onClick={setSelectedProduct}
                groups={groups}
                token={token}
                onGroupChange={async (productId, groupId) => {
                  await apiFetch("/products/" + productId + "/group", token, {
                    method: "PATCH",
                    body: JSON.stringify({ group_id: groupId ?? null }),
                  });
                  setProducts(prev => prev.map(p => p.id === productId ? { ...p, group_id: groupId } : p));
                  loadGroups();
                }}
                onDelete={id => setProducts(prev => prev.filter(p => p.id !== id))}
              />
            </div>
          ))}
          {filteredProducts.length === 0 && (
            <div style={{
              textAlign: "center", padding: "60px 24px",
              background: theme.bg, border: `2px dashed ${theme.line}`, borderRadius: theme.rCard,
            }}>
              {(statFilter || searchQuery || activeGroup !== null) ? (
                <>
                  <p style={{ color: theme.ink, fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Žádný produkt neodpovídá filtru</p>
                  <p style={{ color: theme.mut, fontSize: 14, marginBottom: 18 }}>Zkuste filtr zrušit a zobrazit všechny produkty.</p>
                  <button onClick={() => { setStatFilter(null); setSearchQuery(""); setActiveGroup(null); }} style={{
                    background: theme.blue, border: "none", borderRadius: theme.rBtn,
                    padding: "10px 22px", color: "#fff", fontSize: 14, fontWeight: 600,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>Zrušit filtr</button>
                </>
              ) : (
                <>
                  <p style={{ color: theme.ink, fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Zatím žádné produkty</p>
                  <p style={{ color: theme.mut, fontSize: 14 }}>Přidejte svůj první produkt tlačítkem nahoře.</p>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
